import React, { ReactElement, FC, useState } from 'react'
import { Card, Divider, Typography } from 'antd'
// import { ContactDetails } from '../../../../Components/UI'
import { Attachments } from './Attachments'
import { s } from './style'
import { getAddress } from '../../../../Graphql-Utilities'
import { formatCents } from '../../../../Utilities'
import { useDateTimeDuration } from '../../../../Hooks'
import { ConfirmedIcon } from './ConfirmedIcon'
import { ProposalModal } from '../../ProposalModal'
import {
  ConfirmedBookingEventFragment,
  ConfirmedBookingProposalFragment,
} from './ConfirmedBooking.generated-types'
import { Suppliers } from '../../../../Components/Suppliers'
import { SUPPLIER_DEALS_CONTAINER_DIV } from './styles'
import { SustainableEventBannerWrapper } from '../../../../Components/SustainableEventBannerWrapper'
import { GOOGLE_MAPS, ROUTES } from '../../../../Constants'
import { Link } from 'react-router-dom'

const { Text, Title } = Typography

interface ConfirmedBookingProps {
  event: ConfirmedBookingEventFragment
  proposal: ConfirmedBookingProposalFragment
}

export const ConfirmedBooking: FC<ConfirmedBookingProps> = ({
  event,
  // event: { name, currency, accountManager },
  event: { name, currency, supplierDeals },
  proposal,
  proposal: {
    baseCostTotal,
    space: { spacePhotos, spaceName, venue },
  },
}): ReactElement => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { durationFullDate, durationRange } = useDateTimeDuration(event)

  const address = getAddress(proposal)

  const calculateCostTotalWithSupplierDeals = (): number => {
    return supplierDeals.length
      ? supplierDeals.map((deal) => deal.cost).reduce((a, b) => a + b) +
          baseCostTotal
      : baseCostTotal
  }

  const handleCloseModal = (): void => setIsModalVisible(false)

  return (
    <>
      <div style={s.root}>
        <div>
          <div style={s.firstRow}>
            <Title level={3} style={s.title}>
              {name}
            </Title>
            {/* <Title level={4} style={s.title}>
              {t('eventHub.confirmed.totalSpend', 'Total spend (ex VAT)')}
            </Title> */}
            <Text style={{ fontSize: '24px' }}>{`Total spend (${currency === 'AUD' ? 'inc GST' : 'ex VAT'})`}</Text>
          </div>
          <div style={s.secondRow}>
            <Text style={s.dateTime}>{`${durationFullDate}${
              durationRange ? ', ' + durationRange : ''
            }`}</Text>
            <Text strong>
              {formatCents(currency, calculateCostTotalWithSupplierDeals())}
            </Text>
          </div>
        </div>
        <Card
          style={{
            marginTop: '24px',
          }}
          bodyStyle={s.cardBody}
        >
          {spacePhotos && spacePhotos.length > 2 ? (
            <div style={s.images.root}>
              <img
                style={s.images.primary}
                src={spacePhotos[0].url}
                alt="Main space photo"
                height="334"
              />
              <div style={s.images.side}>
                <img
                  style={s.images.sideImages}
                  src={spacePhotos[1].url}
                  alt="Second space photo"
                  height="167"
                />
                <img
                  style={s.images.sideImages}
                  src={spacePhotos[2].url}
                  alt="Third space photo"
                  height="167"
                />
              </div>
            </div>
          ) : spacePhotos && spacePhotos.length > 0 ? (
            <img
              style={s.image}
              src={spacePhotos[0].url}
              alt="Main space photo"
              height="334"
            />
          ) : undefined}
          <div
            style={{
              margin: '30px',
              display: 'grid',
              gridTemplateColumns: '60px 1fr minmax(80px, 120px) 120px',
              gridTemplateRows: 'auto auto auto',
              gridTemplateAreas:
                '"tick confirmedAt details blank" "tick address address cost"',
              rowGap: 8,
              columnGap: 24,
              fontSize: 18,
              lineHeight: '24px',
            }}
          >
            <ConfirmedIcon style={s.tick} />
            <div
              style={{
                gridArea: 'confirmedAt',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Text style={{ marginBottom: '8px' }}>
                Your booking is confirmed at
              </Text>
              {
                // These shouldn't really be nullable but since they are, it is better to show nothing instead of crashing the whole application if they somehow are null
                venue?.name && spaceName ? (
                  <div>
                    <Text>{`${venue.name}, ${spaceName}`}</Text>
                    <Link
                      to={`${ROUTES.PROPOSAL}/${proposal.UUID}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={{
                        gridArea: 'details',
                        fontSize: 18,
                        fontWeight: 600,
                        color: '#1270B8',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                      }}
                    >
                      View details
                    </Link>
                  </div>
                ) : null
              }
            </div>
            <div
              style={{
                gridArea: 'cost',
                alignSelf: 'end',
                fontSize: 24,
                overflowWrap: 'unset',
              }}
            >
              <Text strong>{formatCents(currency, baseCostTotal)}</Text>
            </div>
            {address && (
              <div
                style={{
                  gridArea: 'address',
                  display: 'flex',
                }}
              >
                <Text>{address}</Text>
                <a
                  href={`${GOOGLE_MAPS}${address}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ color: '#1270B8', paddingLeft: '12px' }}
                >
                  Show on a map
                </a>
                {/* <Link
                  onClick={() => openGoogleMaps(address)}
                  style={{
                    color: '#1270B8',
                    paddingLeft: '12px',
                    paddingRight: '12px',
                  }}
                >
                  Show on a map
                </Link> */}
              </div>
            )}
          </div>
          <Divider style={s.divider} />
          {/* <ContactDetails
            style={s.contactDetails}
            firstName={accountManager?.firstName}
            email={accountManager?.email}
            phone={accountManager?.phoneNumber}
          /> */}
          <Divider style={s.divider} />
          <Attachments proposal={proposal} strong style={s.attachment} />
        </Card>
        <SustainableEventBannerWrapper
          eventUUID={event.UUID}
          marginTop={8}
          marginBottom={8}
        />
        <div style={SUPPLIER_DEALS_CONTAINER_DIV}>
          <Suppliers supplierDeals={supplierDeals} />
        </div>
      </div>
      <ProposalModal
        modalVisible={isModalVisible}
        handleCloseModal={handleCloseModal}
        response={proposal}
        canRequestToBook={false}
        event={event}
      />
    </>
  )
}
